import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { getProvisionedStores } from '../../../../utils/service-calls/fiskaly';
import { ButtonBlack } from '../../../reusable';

const compareDatesDescending = (a, b) => {
  const timeA = a ? new Date(a).getTime() : 0;
  const timeB = b ? new Date(b).getTime() : 0;
  return timeB - timeA;
};

const sortByStatusAndDate = (a, b) => {
  const aActive = a.status === 'ACTIVE';
  const bActive = b.status === 'ACTIVE';
  if (aActive !== bActive) return aActive ? -1 : 1;
  if (aActive) return compareDatesDescending(a.provisionedAt, b.provisionedAt);
  return compareDatesDescending(a.decommissionedAt, b.decommissionedAt);
};

const ViewStores = ({ userToken, onProvisionStore, onViewStore }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [stores, setStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchStores = useCallback(() => {
    setFetchError('');
    setIsLoading(true);
    return Promise.all([
      getProvisionedStores(userToken),
    ])
      .then(([storesRes]) => {
        const sorted = [...(storesRes.body?.stores || [])].sort(sortByStatusAndDate);
        setStores(sorted);
        return sorted;
      })
      .catch((err) => setFetchError(err.message || 'Failed to load stores'))
      .finally(() => setIsLoading(false));
  }, [userToken]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredStores = normalizedSearch
    ? stores.filter((store) => {
      const name = (store.storeName || '').toLowerCase();
      const siret = (store.siret || '').toLowerCase();
      return name.includes(normalizedSearch) || siret.includes(normalizedSearch);
    })
    : stores;

  return (
    <article className="ncss-col-sm-12 ta-sm-l">
      {(() => {
        if (isLoading) return <Loading />;
        if (fetchError) return <section className="text-color-error body-2 mt2-sm">{fetchError}</section>;
        return (
          <section>
            <div
              className="mb2-sm"
              style={{
                alignItems: 'center', display: 'flex', gap: 12, justifyContent: 'space-between',
              }}
            >
              <input
                aria-label="Search stores by name or siret"
                className="ncss-input"
                placeholder="Search by store name or siret"
                style={{
                  border: '1px solid #cccccc',
                  borderRadius: 4,
                  fontSize: 14,
                  padding: '8px 12px',
                  width: 280,
                }}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <ButtonBlack
                className=""
                label="Provision Store"
                onClick={onProvisionStore}
              />
            </div>
            <table className="soa-table">
              <thead>
                <tr>
                  <th>Store Name</th>
                  <th>Siret</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th aria-label="View" style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {filteredStores.length === 0 ? (
                  <tr>
                    <td className="soa-empty" colSpan={4}>
                      {stores.length === 0 ? 'No stores provisioned' : 'No stores match your search'}
                    </td>
                  </tr>
                ) : (
                  filteredStores.map((store, idx) => (
                    <tr key={store.storeId} className={idx % 2 === 0 ? 'soa-row-even' : ''}>
                      <td>{store.storeName}</td>
                      <td>{store.siret}</td>
                      <td>{store.status}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="soa-row-edit-toggle"
                          title="View"
                          type="button"
                          onClick={() => onViewStore(store)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        );
      })()}
    </article>
  );
};

ViewStores.propTypes = {
  onProvisionStore: PropTypes.func.isRequired,
  onViewStore: PropTypes.func.isRequired,
  userToken: PropTypes.string.isRequired,
};

export default ViewStores;
